import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import type { CustomerAuditLog } from "@/types/collections";

interface CustomerAuditLogApi {
  id: number;
  customerId: number;
  actionType: string;
  description: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  metadata: string | null;
  createdByUserId: number;
  createdByUserName: string;
  createdAt: string;
}

const actionTypeToActionMap: Record<string, CustomerAuditLog["action"]> = {
  onstopchanged: "on_stop_change",
  collectionstrategychanged: "collection_strategy_change",
  creditcontrollerchanged: "credit_controller_change",
  accountmanagerchanged: "account_manager_change",
  risklevelchanged: "risk_level_change",
  noteadded: "note_added",
};

function mapApiCustomerAuditLog(api: CustomerAuditLogApi): CustomerAuditLog {
  const actionName = api.actionType.toLowerCase().replace(/\s+/g, "");
  return {
    id: String(api.id),
    customerId: String(api.customerId),
    action: actionTypeToActionMap[actionName] || "field_update",
    description: api.description,
    fieldName: api.fieldName || undefined,
    oldValue: api.oldValue,
    newValue: api.newValue,
    createdBy: api.createdByUserName || "System",
    createdAt: new Date(api.createdAt),
  };
}

function mockCustomerAuditLogs(customerId: string): CustomerAuditLog[] {
  return [
    {
      id: "audit-1",
      customerId,
      action: "on_stop_change",
      description: "Customer placed on stop",
      fieldName: "OnStop",
      oldValue: "False",
      newValue: "True",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 22, 9, 15),
    },
    {
      id: "audit-2",
      customerId,
      action: "collection_strategy_change",
      description: "Collection strategy changed",
      fieldName: "CollectionStrategy",
      oldValue: "Standard",
      newValue: "Intensive",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 20, 14, 30),
    },
    {
      id: "audit-3",
      customerId,
      action: "credit_controller_change",
      description: "Credit controller changed",
      fieldName: "CreditController",
      oldValue: "John Smith",
      newValue: "Sarah Johnson",
      createdBy: "System",
      createdAt: new Date(2024, 2, 19, 11, 20),
    },
    {
      id: "audit-4",
      customerId,
      action: "risk_level_change",
      description: "Risk level changed",
      fieldName: "RiskLevel",
      oldValue: "Low",
      newValue: "High",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 18, 16, 45),
    },
    {
      id: "audit-5",
      customerId,
      action: "note_added",
      description: "Note added to customer",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 15, 8, 30),
    },
  ];
}

export function useCustomerAuditLogs(customerId: string | undefined) {
  const { apiGet } = useApi();

  const mockLogs = customerId ? mockCustomerAuditLogs(customerId) : [];

  return useModeAwareQuery<CustomerAuditLog[], Error>(
    ["customer-audit-logs", customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      const response = await apiGet<CustomerAuditLogApi[]>(
        "/api/v1/audit-logs/customers",
        { customerId },
        signal
      );
      return response.map(mapApiCustomerAuditLog);
    },
    mockLogs,
    { enabled: !!customerId, staleTime: 30 * 1000, refetchOnWindowFocus: false }
  );
}
